export interface SkillOrInterest {
  name: string;
  category: string;
}

export interface CategoryGroup {
  category: string;
  items: string[];
}

export const SKILL_CATEGORIES: CategoryGroup[] = [
  {
    category: "Core Professional Skills",
    items: [
      "Communication (verbal)", "Communication (written)", "Active listening", "Time management",
      "Organisation", "Problem-solving", "Critical thinking", "Decision-making", "Attention to detail",
      "Adaptability", "Multitasking", "Accountability", "Professionalism", "Emotional intelligence",
      "Work ethic", "Initiative", "Resilience", "Conflict resolution", "Negotiation", "Stakeholder management"
    ]
  },
  {
    category: "Office & Administrative Skills",
    items: [
      "Calendar management", "Email management", "Data entry", "Document management",
      "Filing systems (digital and physical)", "Meeting coordination", "Minute taking", "Office coordination",
      "Travel booking", "Expense processing", "Reception handling", "Phone etiquette", "Record keeping",
      "Inventory tracking", "Office supplies management", "Scheduling", "CRM usage", "Mail handling",
      "Report formatting", "Presentation preparation"
    ]
  },
  {
    category: "Project Management Skills",
    items: [
      "Project planning", "Task prioritisation", "Risk management", "Budget tracking", "Resource allocation",
      "Timeline management", "Agile methodology", "Scrum practices", "Waterfall methodology", "Scope management",
      "Change management", "Project documentation", "Stakeholder reporting", "KPI tracking", "Workflow design",
      "Dependency management", "Milestone tracking", "Project scheduling tools", "Cross-functional coordination",
      "Post-project evaluation"
    ]
  },
  {
    category: "Finance & Accounting Skills",
    items: [
      "Financial reporting", "Budgeting", "Forecasting", "Cost control", "Accounts payable",
      "Accounts receivable", "Reconciliation", "Payroll processing", "Tax compliance", "Financial analysis",
      "Cash flow management", "Variance analysis", "Audit preparation", "General ledger management",
      "Invoice processing", "Expense auditing", "Financial modelling", "Profit and loss analysis",
      "Compliance reporting", "Asset management"
    ]
  },
  {
    category: "Contracts & Estimating Skills",
    items: [
      "Cost estimation", "Tender preparation", "Contract drafting", "Contract review",
      "Risk assessment (contracts)", "Scope analysis", "Pricing strategy", "Supplier negotiation",
      "Procurement processes", "Bid evaluation", "Quantity take-offs", "Variation management",
      "Compliance with standards", "Subcontractor management", "Cost-benefit analysis", "Project costing",
      "Claims management", "Contract lifecycle management", "Documentation control", "Legal awareness (contracts)"
    ]
  },
  {
    category: "HR & People Management Skills",
    items: [
      "Recruitment", "Interviewing", "Onboarding", "Offboarding", "Employee relations",
      "Performance management", "Training and development", "Policy development", "HR compliance",
      "Conflict mediation", "Workforce planning", "Employee engagement", "Diversity and inclusion",
      "HRIS systems", "Disciplinary procedures", "Compensation planning", "Benefits administration",
      "Talent management", "Succession planning", "Organisational development"
    ]
  },
  {
    category: "Digital & Technical Skills",
    items: [
      "Microsoft Excel (advanced)", "Microsoft Word", "Microsoft PowerPoint", "Microsoft Outlook",
      "Data analysis", "Data visualisation", "CRM systems", "ERP systems", "Cloud tools", "Automation tools",
      "Database management", "Cybersecurity awareness", "Software troubleshooting", "Reporting dashboards",
      "AI tools usage", "Workflow automation", "File sharing systems", "Version control", "Basic coding",
      "System integration awareness"
    ]
  },
  {
    category: "Business & Strategy Skills",
    items: [
      "Strategic planning", "Business analysis", "Market research", "Competitive analysis",
      "Process improvement", "Lean principles", "Six Sigma basics", "Change leadership", "Innovation thinking",
      "Business case development", "ROI analysis", "Customer journey mapping", "Growth strategy",
      "Risk analysis", "Operational planning", "Policy implementation", "Governance understanding",
      "KPI development", "Decision frameworks", "Business modelling"
    ]
  },
  {
    category: "Customer & Service Skills",
    items: [
      "Customer service", "Complaint handling", "Relationship management", "Client onboarding",
      "Client retention", "Service delivery", "Customer communication", "Feedback analysis",
      "Service improvement", "Account management", "Conflict handling (clients)", "Professional etiquette",
      "Empathy", "Customer satisfaction tracking", "Escalation management", "Service-level agreement management",
      "Client reporting", "Negotiation with clients", "Presentation to clients", "Customer experience design"
    ]
  },
  {
    category: "Personal Productivity & Leadership Skills",
    items: [
      "Leadership", "Delegation", "Coaching", "Mentoring", "Team collaboration", "Facilitation",
      "Public speaking", "Decision accountability", "Goal setting", "Self-management", "Stress management",
      "Creativity", "Innovation mindset", "Influence and persuasion", "Networking", "Cultural awareness",
      "Ethical judgement", "Learning agility", "Feedback delivery", "Continuous improvement mindset"
    ]
  },
  {
    category: "Marketing & Social Media Skills",
    items: [
      "Marketing strategy development", "Campaign planning", "Brand positioning", "Audience segmentation",
      "Content strategy", "Go-to-market strategy", "Funnel design", "Lead generation strategy",
      "Product marketing", "Growth marketing", "Copywriting", "Storytelling", "Video scripting",
      "Graphic design basics", "Short-form video creation", "Content calendar planning", "Blog writing",
      "Email marketing content", "Ad creative development", "Visual branding consistency",
      "Social media scheduling", "Community management", "Engagement strategy", "Hashtag strategy",
      "Platform-specific optimisation", "Influencer collaboration", "Social listening", "Trend analysis",
      "Comment moderation", "Direct message communication handling", "Facebook and Instagram Ads",
      "Google Ads", "LinkedIn Ads", "Conversion tracking", "A/B testing", "Campaign optimisation",
      "SEO", "SEM", "Google Analytics", "ROI tracking for campaigns", "Marketing automation",
      "CRM marketing integration", "AI content tools usage", "Retargeting campaigns",
      "Landing page optimisation", "Conversion rate optimisation", "Brand analytics",
      "Customer persona development", "Online reputation management", "Viral marketing techniques"
    ]
  },
  {
    category: "Safety Professional Skills",
    items: [
      "Workplace health and safety knowledge", "Risk assessment", "Hazard identification",
      "Incident investigation", "Safety audits", "Safety inspections", "Compliance monitoring",
      "Safety reporting", "Emergency response planning", "Safety management systems",
      "Working at heights compliance", "Scaffold inspection", "Scaffold compliance",
      "Safe Work Method Statements", "Job Safety Analysis / Job Hazard Analysis", "Permit-to-work systems",
      "Fall protection systems", "Toolbox talks delivery", "Site safety coordination",
      "Temporary works safety", "Safety training delivery", "Induction programs", "Safety communication",
      "Behaviour-based safety", "Safety leadership", "Worker engagement in safety", "Safety coaching",
      "Conflict handling (safety enforcement)", "Safety awareness campaigns", "Documentation of procedures",
      "Regulatory compliance", "Incident reporting systems", "Root cause analysis",
      "Corrective action planning", "Safety KPI tracking", "Return-to-work programs",
      "Workers compensation processes", "Environmental health and safety", "Legal awareness in safety",
      "Contractor safety management", "Safety data analysis", "Digital safety systems",
      "Risk mitigation strategies", "Safety culture development", "ISO 45001 understanding",
      "Fatigue management", "Mental health awareness in the workplace",
      "Safety innovation and technology adoption", "Crisis management", "Continuous safety improvement"
    ]
  },
  {
    category: "IT Professional & CTO Skills",
    items: [
      "IT support management", "Help desk coordination", "Systems administration", "Network administration",
      "Infrastructure management", "Hardware lifecycle management", "Software deployment",
      "User access management", "Identity and access management", "Endpoint management",
      "Cybersecurity awareness", "Security policy development", "Risk management for IT systems",
      "Vulnerability management", "Incident response planning", "Data protection",
      "Backup and disaster recovery", "Business continuity planning", "Access control design",
      "Security compliance management", "Software architecture understanding",
      "Application lifecycle management", "API integration oversight", "Database administration",
      "Cloud infrastructure management", "DevOps practices", "Version control management",
      "QA process oversight", "Systems integration", "Technical troubleshooting",
      "Data governance", "Data analytics oversight", "Dashboard and reporting systems",
      "Data quality management", "Business intelligence tools", "KPI reporting for technology teams",
      "Systems performance monitoring", "Capacity planning", "Data privacy management",
      "Master data management awareness", "IT project management", "Digital transformation planning",
      "Technology roadmap development", "Vendor management", "IT procurement",
      "Budget management for IT", "Resource planning for technical teams", "Agile delivery leadership",
      "Change management for systems", "Technology implementation planning", "Technology strategy",
      "Enterprise architecture thinking", "Innovation leadership", "Product and engineering alignment",
      "Technical team leadership", "Engineering culture development", "Scalability planning",
      "Technology governance", "Executive stakeholder communication", "Build versus buy decision-making",
      "Cloud cost optimisation", "Platform engineering", "Site reliability engineering awareness",
      "Observability strategy", "Microservices architecture", "Solution architecture",
      "Technical debt management", "Release management", "Environment management", "Automation strategy",
      "AI implementation strategy", "Data architecture", "Information security governance",
      "Penetration testing awareness", "Compliance with ISO 27001", "SaaS platform management",
      "Multi-system integration strategy", "Technical hiring", "Engineering performance management",
      "Digital product strategy"
    ]
  }
];

export const INTEREST_CATEGORIES: CategoryGroup[] = [
  {
    category: "Learning & Development",
    items: [
      "Personal development", "Leadership development", "Continuous learning", "Reading business books",
      "Attending workshops", "Online courses", "Skill-building", "Coaching and mentoring",
      "Professional certifications", "Industry trends"
    ]
  },
  {
    category: "Business & Career Interests",
    items: [
      "Entrepreneurship", "Startups", "Business strategy", "Innovation", "Process improvement",
      "Productivity optimisation", "Networking", "Career growth", "Thought leadership", "Public speaking"
    ]
  },
  {
    category: "Technology & Innovation",
    items: [
      "Artificial intelligence", "Automation", "Software development", "Emerging technologies",
      "Cybersecurity", "Data analytics", "SaaS platforms", "Digital transformation", "Tech gadgets",
      "App development"
    ]
  },
  {
    category: "Marketing & Creative Interests",
    items: [
      "Content creation", "Social media trends", "Branding", "Graphic design", "Video production",
      "Photography", "Copywriting", "Storytelling", "Influencer marketing", "Creative campaigns"
    ]
  },
  {
    category: "Finance & Analytical Interests",
    items: [
      "Investing", "Financial markets", "Budgeting", "Cryptocurrency", "Economic trends",
      "Financial planning", "Wealth building", "Risk analysis", "Business finance", "Property investment"
    ]
  },
  {
    category: "Industry & Trade Interests",
    items: [
      "Construction industry", "Scaffolding innovation", "Safety systems", "Engineering solutions",
      "Infrastructure projects", "Building technology", "Equipment and machinery", "Workplace safety",
      "Compliance standards", "Project delivery"
    ]
  },
  {
    category: "Safety & Compliance Interests",
    items: [
      "Workplace safety improvement", "Risk prevention", "Safety innovation", "Health and wellbeing at work",
      "Safety leadership", "Compliance systems", "Incident prevention", "Environmental safety",
      "Safety technology", "WHS regulations"
    ]
  },
  {
    category: "People & Culture Interests",
    items: [
      "Team building", "Employee engagement", "Workplace culture", "Diversity and inclusion",
      "Talent development", "Organisational psychology", "Communication skills", "Conflict resolution",
      "Leadership coaching", "Mentorship"
    ]
  },
  {
    category: "Productivity & Performance",
    items: [
      "Time management techniques", "Goal setting", "High performance habits", "Efficiency systems",
      "Workflow optimisation", "Habit tracking", "Focus techniques", "Personal organisation",
      "Decision-making frameworks", "Performance improvement"
    ]
  },
  {
    category: "Community & Social Impact",
    items: [
      "Volunteering", "Community development", "Youth programs", "Charity work", "Social enterprises",
      "Non-profit initiatives", "Education access", "Global development", "Environmental sustainability",
      "Philanthropy"
    ]
  },
  {
    category: "Creative & Lifestyle Interests",
    items: [
      "Writing", "Blogging", "Podcasting", "Music", "Art", "Design", "Fashion", "Cooking", "Travel",
      "Photography"
    ]
  },
  {
    category: "Health & Wellbeing",
    items: [
      "Fitness", "Mental health", "Mindfulness", "Meditation", "Nutrition", "Work-life balance",
      "Stress management", "Sports", "Outdoor activities", "Wellness routines"
    ]
  },
  {
    category: "Social & Networking",
    items: [
      "Networking events", "Professional communities", "Industry meetups", "Online communities",
      "Collaboration", "Relationship building", "Public forums", "Knowledge sharing",
      "Group discussions", "Community leadership"
    ]
  },
  {
    category: "Innovation & Future Thinking",
    items: [
      "Future of work", "Smart cities", "Sustainability innovation", "AI in business",
      "Digital economies", "Automation in industries", "Remote work trends", "Business disruption",
      "Startup ecosystems", "Emerging markets"
    ]
  },
  {
    category: "General Knowledge & Curiosity",
    items: [
      "History", "Psychology", "Philosophy", "Politics", "Economics", "Science", "Technology news",
      "Global affairs", "Human behaviour", "Lifelong learning"
    ]
  }
];
